import React, { useEffect, useMemo, useState } from 'react';
import parse, { domToReact } from 'html-react-parser';
import FIBInput from './FIBInput';
import { debounce, isEmpty } from 'lodash';
import { useContextEffect, useStableCallback } from '../../../hooks';
import ReadingComprehensionElement from './ReadingComprehensionElement';
import { renderKatex } from '../../../utils/domUtils';

const extractBlankIds = (html) => {
  if (!html) return [];
  const ids = [];
  const regex = /blankid=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    ids.push(match[1]);
  }
  return ids;
};

const FIBQuestionBody = ({
  questionId,
  elements,
  answers,
  updateAnswer,
  updateAndSaveAnswer,
  readingComprehensionQuestionRef
}) => {
  const [blankStates, setBlankStates] = useState({});
  const fibText = elements?.[0]?.html;
  const blankIds = useMemo(() => extractBlankIds(fibText), [fibText]);

  useEffect(() => {
    if (answers && !isEmpty(answers)) {
      setBlankStates(answers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateBlankStates = useStableCallback((blankId, answer) => {
    setBlankStates((state) => {
      if (state?.[blankId]?.[0] === answer) return state;

      return {
        ...state,
        [blankId]: [answer]
      };
    });
  });

  const debouncedSave = useMemo(() => debounce(updateAndSaveAnswer, 3000), [updateAndSaveAnswer]);

  useContextEffect({
    deps: [blankStates],
    ctx: { questionId, debouncedSave },
    cb: (ctx) => {
      ctx.debouncedSave(ctx.questionId, blankStates);

      return () => {
        ctx?.debouncedSave?.cancel();
      };
    }
  });

  useContextEffect({
    deps: [blankStates],
    ctx: { questionId, updateAnswer },
    cb: (ctx) => {
      ctx.updateAnswer(ctx.questionId, blankStates);
    }
  });

  const error = useMemo(() => {
    if (isEmpty(blankStates)) return '';
    const hasEmptyValue = Object.values(blankStates).some((blank) => blank === '' || blank?.[0] === '');
    if (hasEmptyValue) {
      return 'Blanks are left empty. For a score, please fill in all the blanks.';
    }
    return '';
  }, [blankStates]);

  const parseReplaceOptions = useMemo(
    () => ({
      replace: (domNode) => {
        if (!domNode) return;

        switch (domNode.name) {
          case 'p':
            return (
              <p className="leading-relaxed text-foreground">
                {domToReact(domNode?.children, parseReplaceOptions)}
              </p>
            );
          case 'ol':
            return (
              <ol className="my-2 list-decimal pl-5">
                {domToReact(domNode?.children, parseReplaceOptions)}
              </ol>
            );
          case 'li':
            return (
              <li className="my-2 leading-relaxed">
                {domToReact(domNode?.children, parseReplaceOptions)}
              </li>
            );
          case 'fill-in-blanks': {
            const blankId = domNode.attribs.blankid;
            const blankIndex = blankIds.indexOf(blankId);
            return (
              <span className="mx-px inline-flex align-middle items-center rounded border-2 border-dashed border-gray-300 bg-gray-100 px-1.5 py-0.5">
                <span className="text-sm font-medium text-primary">
                  ({blankIndex >= 0 ? blankIndex + 1 : '?'})
                </span>
              </span>
            );
          }
          default:
            break;
        }

        const katexRendered = renderKatex(domNode);
        if (katexRendered !== undefined) {
          return katexRendered;
        }
      }
    }),
    [blankIds]
  );

  const parsedHtmlContent = useMemo(() => {
    return parse(fibText, parseReplaceOptions);
  }, [fibText, parseReplaceOptions]);

  return (
    <div className="katex-question w-full space-y-4">
      {readingComprehensionQuestionRef && (
        <ReadingComprehensionElement questionRef={readingComprehensionQuestionRef} />
      )}

      <div className="space-y-4">
        <div className="text-base leading-relaxed text-foreground md:text-lg">{parsedHtmlContent}</div>

        <div className="space-y-2 md:space-y-3">
          <h4 className="font-medium text-foreground">Fill in the blanks:</h4>
          {blankIds.map((blankId, index) => (
            <div key={blankId} className="flex items-center gap-3">
              <span className="min-w-[60px] text-sm font-medium text-muted-foreground">
                Blank {index + 1}:
              </span>
              <FIBInput
                blankId={blankId}
                initialValue={answers?.[blankId]?.[0]}
                onChange={updateBlankStates}
                placeholder={`Answer for blank ${index + 1}...`}
              />
            </div>
          ))}
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default FIBQuestionBody;
